// src/pages/AdminHubPage.tsx
import { useEffect, useMemo, useState } from 'react';
import { supabase, type Restaurant, type MenuItem } from '../lib/supabase';
import {
  RefreshCw, Lock, LogOut, Copy, Send, Eye, EyeOff, CheckCircle2, XCircle
} from 'lucide-react';

type Driver = {
  id: string;
  name: string;
  phone: string | null;
  vehicle_type: 'bike' | 'moped' | 'car';
  status: 'pending' | 'approved' | 'active' | 'inactive';
};

type Order = {
  id: string;
  restaurant_id: string;
  customer_name: string;
  customer_phone: string | null;
  customer_address: string;
  zone: string | null;
  delivery_fee: number;
  subtotal: number;
  total: number;
  status: 'pending' | 'assigned' | 'delivering' | 'completed' | 'cancelled';
  driver_id: string | null;
  created_at: string;
};

type OrderItemRow = {
  id: string;
  order_id: string;
  quantity: number;
  price: number;
  size?: string | null;
  menu_item_id: string;
  menu_items?: { name: string } | null;
};

type TabKey = 'overview' | 'assign' | 'restaurant';

const ADMIN_PASS = import.meta.env.VITE_ADMIN_PASS;

export default function AdminHubPage() {
  // Gate
  const [isAuthed, setIsAuthed] = useState<boolean>(() => localStorage.getItem('admin_ok') === '1');
  const [enteredPw, setEnteredPw] = useState('');

  // UI
  const [tab, setTab] = useState<TabKey>('overview');
  const [loading, setLoading] = useState(false);

  // Data: felles
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [itemsByOrder, setItemsByOrder] = useState<Record<string, OrderItemRow[]>>({});

  // Oppdrag
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [pickupName, setPickupName] = useState<string>('');
  const [payout, setPayout] = useState<number>(70);
  const [paid, setPaid] = useState<boolean>(true);

  // Restaurant-konsoll
  const [ridInput, setRidInput] = useState('');
  const [rConsole, setRConsole] = useState<Restaurant | null>(null);
  const [rMenu, setRMenu] = useState<MenuItem[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    if (!isAuthed) return;
    void refreshOverview();
  }, [isAuthed]);

  useEffect(() => {
    if (!rConsole || !autoRefresh) return;
    const id = setInterval(() => void refreshRestaurantConsole(rConsole.id), 20000);
    return () => clearInterval(id);
  }, [rConsole, autoRefresh]);

  async function login() {
    if (enteredPw.trim() && ADMIN_PASS && enteredPw.trim() === String(ADMIN_PASS)) {
      localStorage.setItem('admin_ok', '1');
      setIsAuthed(true);
    } else {
      alert('Feil passord');
    }
  }

  function logout() {
    localStorage.removeItem('admin_ok');
    setIsAuthed(false);
    setEnteredPw('');
  }

  // ---------- Fetch felles ----------
  async function refreshOverview() {
    setLoading(true);
    const [{ data: dRestaurants }, { data: dDrivers }, { data: dOrders }] = await Promise.all([
      supabase.from('restaurants').select('id, name, logo_url, description, is_active, created_at').order('created_at', { ascending: false }),
      supabase.from('drivers').select('id, name, phone, vehicle_type, status').in('status', ['approved', 'active']).order('name', { ascending: true }),
      supabase.from('orders')
        .select('id, restaurant_id, customer_name, customer_phone, customer_address, zone, delivery_fee, subtotal, total, status, driver_id, created_at')
        .order('created_at', { ascending: false })
        .limit(200),
    ]);

    setRestaurants(dRestaurants || []);
    setDrivers(dDrivers || []);
    setOrders(dOrders || []);

    const ids = (dOrders || []).map(o => o.id);
    if (ids.length) {
      const { data: dItems } = await supabase
        .from('order_items')
        .select('id, order_id, quantity, price, size, menu_item_id, menu_items(name)')
        .in('order_id', ids);
      const grouped: Record<string, OrderItemRow[]> = {};
      (dItems || []).forEach(it => {
        (grouped[it.order_id] ||= []).push(it);
      });
      setItemsByOrder(grouped);
    } else {
      setItemsByOrder({});
    }
    setLoading(false);
  }

  // ---------- Oppdrag ----------
  useEffect(() => {
    if (!selectedOrderId) return;
    const ord = orders.find(o => o.id === selectedOrderId);
    if (!ord) return;
    setPickupName((ord.customer_name || '').split(' ')[0] || '');
    setPayout(Math.max(60, Math.round(ord.delivery_fee || 70)));
  }, [selectedOrderId, orders]);

  const selectedOrder = useMemo(() => orders.find(o => o.id === selectedOrderId) || null, [orders, selectedOrderId]);
  const selectedDriver = useMemo(() => drivers.find(d => d.id === selectedDriverId) || null, [drivers, selectedDriverId]);
  const selectedItems = useMemo(() => (selectedOrder ? itemsByOrder[selectedOrder.id] || [] : []), [selectedOrder, itemsByOrder]);

  const driverMessage = useMemo(() => {
    if (!selectedOrder || !selectedDriver) return '';
    const lines = selectedItems.map((it, idx) => {
      const qty = it.quantity > 1 ? `${it.quantity}x ` : '';
      const size = it.size ? ` (${String(it.size).toLowerCase()})` : '';
      return `${idx + 1}. ${qty}${it.menu_items?.name || 'Ukjent vare'}${size}`;
    });
    const paidTxt = paid ? 'betalt' : 'ikke betalt';
    const firstNameDriver = selectedDriver.name.split(' ')[0] || selectedDriver.name;
    return `Hei ${firstNameDriver}, ny leveranse! ${selectedOrder.customer_name} - ${selectedOrder.customer_address}.
${lines.join('\n')}
Status: ${paidTxt}, hent under navnet ${pickupName}.
Du tjener: ${Math.round(payout)},- uten mulige tips.`;
  }, [selectedOrder, selectedDriver, selectedItems, pickupName, payout, paid]);

  async function assignDriver() {
    if (!selectedOrder || !selectedDriver) {
      alert('Velg både ordre og sjåfør');
      return;
    }
    setLoading(true);
    const { error } = await supabase
      .from('orders')
      .update({ driver_id: selectedDriver.id, status: 'assigned' })
      .eq('id', selectedOrder.id);
    setLoading(false);
    if (error) {
      alert('Klarte ikke tildele oppdrag.');
      return;
    }
    // lokal oppdatering
    setOrders(prev => prev.map(o => o.id === selectedOrder.id ? { ...o, driver_id: selectedDriver.id, status: 'assigned' } : o));
    alert('Oppdrag tildelt ✅');
  }

  async function copyMsg() {
    if (!driverMessage) return;
    try {
      await navigator.clipboard.writeText(driverMessage);
      alert('Melding kopiert 👍');
    } catch {
      alert('Kunne ikke kopiere. Marker og kopier manuelt.');
    }
  }

  // ---------- Restaurant-konsoll (inne i admin) ----------
  async function openRestaurantConsole() {
    const rid = ridInput.trim();
    if (!rid) {
      alert('Skriv inn restaurant-ID');
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.from('restaurants').select('*').eq('id', rid).maybeSingle();
    if (error || !data) {
      setLoading(false);
      alert('Fant ikke restaurant.');
      return;
    }
    setRConsole(data as Restaurant);
    await refreshRestaurantConsole(rid);
    setLoading(false);
  }

  async function refreshRestaurantConsole(restaurantId: string) {
    const [{ data: dMenu }] = await Promise.all([
      supabase
        .from('menu_items')
        .select('id, name, description, price, category, is_available')
        .eq('restaurant_id', restaurantId)
        .order('category', { ascending: true })
        .order('name', { ascending: true }),
    ]);
    setRMenu(dMenu || []);
  }

  async function toggleRActive() {
    if (!rConsole) return;
    const next = !rConsole.is_active;
    const { error } = await supabase.from('restaurants').update({ is_active: next }).eq('id', rConsole.id);
    if (error) {
      alert('Kunne ikke oppdatere status');
      return;
    }
    setRConsole({ ...rConsole, is_active: next });
  }

  async function toggleRItem(itemId: string, current: boolean) {
    const { error } = await supabase.from('menu_items').update({ is_available: !current }).eq('id', itemId);
    if (error) {
      alert('Kunne ikke oppdatere vare');
      return;
    }
    setRMenu(prev => prev.map(m => (m.id === itemId ? { ...m, is_available: !current } : m)));
  }

  const groupedRMenu = useMemo(() => {
    return rMenu.reduce((acc, m) => {
      const cat = m.category || 'Annet';
      (acc[cat] ||= []).push(m);
      return acc;
    }, {} as Record<string, MenuItem[]>);
  }, [rMenu]);

  // ================== RENDER ==================
  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <Lock className="w-10 h-10 text-[#004D99] dark:text-orange-400 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2">Admin</h1>
          <p className="text-center text-gray-600 dark:text-gray-400 mb-4">Skriv inn passord.</p>
          <input
            type="password"
            value={enteredPw}
            onChange={e => setEnteredPw(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white mb-3"
            placeholder="••••••••"
          />
          <button
            onClick={login}
            className="w-full py-3 rounded-lg bg-[#004D99] hover:bg-[#003366] text-white font-semibold"
          >
            Logg inn
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
      <div className="max-w-7xl mx-auto px-4 space-y-6">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Hub</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void refreshOverview()}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white dark:bg-white dark:text-gray-900"
            >
              <RefreshCw className="w-4 h-4" />
              Oppdater
            </button>
            <button
              onClick={logout}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100"
              title="Logg ut"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {([
            { k: 'overview', t: 'Oversikt' },
            { k: 'assign', t: 'Oppdrag' },
            { k: 'restaurant', t: 'Restaurant-konsoll' },
          ] as { k: TabKey; t: string }[]).map(({ k, t }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                tab === k ? 'bg-[#004D99] text-white' : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'overview' && (
          <section className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Restauranter</h2>
              <span className="text-sm px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                {restaurants.length} stk
              </span>
            </div>

            {loading && <div className="p-3 text-gray-500">Laster…</div>}

            {!loading && (
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 dark:text-gray-300">
                      <th className="py-2 pr-4">Logo</th>
                      <th className="py-2 pr-4">Navn</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Beskrivelse</th>
                      <th className="py-2 pr-4">Opprettet</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {restaurants.map(r => (
                      <tr key={r.id} className="text-gray-900 dark:text-gray-100">
                        <td className="py-2 pr-4">
                          {r.logo_url ? (
                            <img src={r.logo_url} alt={r.name} className="w-10 h-10 rounded object-contain bg-white" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-gray-200 dark:bg-gray-700" />
                          )}
                        </td>
                        <td className="py-2 pr-4 font-medium">{r.name}</td>
                        <td className="py-2 pr-4">
                          <span
                            className={`px-2 py-0.5 rounded text-xs ${
                              r.is_active
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {r.is_active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td className="py-2 pr-4 max-w-[420px] truncate">{r.description || '—'}</td>
                        <td className="py-2 pr-4">{new Date(r.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                    {!restaurants.length && (
                      <tr>
                        <td colSpan={5} className="py-3 text-gray-500">Ingen restauranter funnet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {tab === 'assign' && (
          <section className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Tildel oppdrag</h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Ordre</label>
                  <select
                    value={selectedOrderId}
                    onChange={e => setSelectedOrderId(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2"
                  >
                    <option value="">— Velg ordre —</option>
                    {orders.map(o => (
                      <option key={o.id} value={o.id}>
                        {o.customer_name} • {o.customer_address} • {o.total} kr • {o.status}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Sjåfør</label>
                  <select
                    value={selectedDriverId}
                    onChange={e => setSelectedDriverId(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2"
                  >
                    <option value="">— Velg sjåfør —</option>
                    {drivers.map(d => (
                      <option key={d.id} value={d.id}>
                        {d.name} {d.phone ? `• ${d.phone}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Hent under navnet</label>
                  <input
                    type="text"
                    value={pickupName}
                    onChange={e => setPickupName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2"
                    placeholder="F.eks. Thomas"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <label className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300">
                    <input type="checkbox" checked={paid} onChange={e => setPaid(e.target.checked)} className="h-4 w-4" />
                    Betalt
                  </label>

                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Utbetaling (kr)</label>
                    <input
                      type="number"
                      min={0}
                      value={payout}
                      onChange={e => setPayout(Number(e.target.value))}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={assignDriver}
                    disabled={!selectedOrderId || !selectedDriverId}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-400"
                  >
                    <Send className="w-4 h-4" />
                    Tildel oppdrag
                  </button>
                  <button
                    onClick={copyMsg}
                    disabled={!driverMessage}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#004D99] text-white disabled:bg-gray-400"
                  >
                    <Copy className="w-4 h-4" />
                    Kopier melding
                  </button>
                  {selectedDriver?.phone && driverMessage && (
                    <a
                      href={`sms:${encodeURIComponent(selectedDriver.phone)}?&body=${encodeURIComponent(driverMessage)}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                    >
                      Åpne i SMS
                    </a>
                  )}
                </div>
              </div>

              <div className="flex flex-col">
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Forhåndsvisning</label>
                <textarea
                  rows={14}
                  readOnly
                  value={selectedOrderId && selectedDriverId ? driverMessage : 'Velg både ordre og sjåfør for å generere melding.'}
                  className="flex-1 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                />
                <div className="mt-3 rounded bg-gray-50 dark:bg-gray-900 p-3 text-sm">
                  {selectedOrder ? (
                    <>
                      <div className="font-semibold mb-1">Varer</div>
                      <ul className="list-disc ml-5 space-y-0.5">
                        {(selectedItems || []).map((it, idx) => (
                          <li key={it.id}>
                            {idx + 1}. {it.quantity > 1 ? `${it.quantity}x ` : ''}
                            {it.menu_items?.name || 'Ukjent vare'}
                            {it.size ? ` (${String(it.size).toLowerCase()})` : ''} – {(it.price * it.quantity).toFixed(2)} kr
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <div className="text-gray-500">Ingen ordre valgt.</div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {tab === 'restaurant' && (
          <section className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow space-y-5">
            {!rConsole ? (
              <div className="max-w-lg">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Restaurant-konsoll</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-3">Logg inn med restaurant-ID.</p>
                <div className="flex gap-2">
                  <input
                    value={ridInput}
                    onChange={e => setRidInput(e.target.value)}
                    placeholder="f.eks. 0e107de2-b0c1-4e44-aacd-65ead712e73b"
                    className="flex-1 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={openRestaurantConsole}
                    className="px-4 py-3 rounded-lg bg-[#004D99] hover:bg-[#003366] text-white"
                  >
                    Åpne
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {rConsole.logo_url ? (
                      <img src={rConsole.logo_url} alt={rConsole.name} className="w-12 h-12 rounded object-contain bg-white" />
                    ) : (
                      <div className="w-12 h-12 rounded bg-gray-200 dark:bg-gray-700" />
                    )}
                    <div>
                      <div className="text-xl font-bold text-gray-900 dark:text-white">{rConsole.name}</div>
                      <div className="text-sm">
                        {rConsole.is_active ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-300">
                            <CheckCircle2 className="w-4 h-4" /> Aktiv
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300">
                            <XCircle className="w-4 h-4" /> Inaktiv
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={toggleRActive}
                      className={`px-3 py-2 rounded-lg text-white ${rConsole.is_active ? 'bg-rose-600 hover:bg-rose-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                    >
                      {rConsole.is_active ? 'Sett Inaktiv' : 'Sett Aktiv'}
                    </button>
                    <button
                      onClick={() => void refreshRestaurantConsole(rConsole.id)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Oppdater
                    </button>
                    <button
                      onClick={() => setRConsole(null)}
                      className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100"
                    >
                      Logg ut denne
                    </button>
                    <button
                      onClick={() => setAutoRefresh(p => !p)}
                      className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100"
                      title="Slå av/på auto-oppdatering"
                    >
                      Auto: {autoRefresh ? 'På' : 'Av'}
                    </button>
                  </div>
                </div>

                {Object.entries(groupedRMenu).map(([cat, items]) => (
                  <div key={cat} className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">{cat}</h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {items.map(item => (
                        <div key={item.id} className="rounded border border-gray-200 dark:border-gray-700 p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">{item.name}</div>
                              {item.description && (
                                <div className="text-xs text-gray-500 line-clamp-2">{item.description}</div>
                              )}
                              <div className="text-sm text-gray-700 dark:text-gray-200 mt-1">
                                {item.price?.toFixed ? item.price.toFixed(2) : item.price} kr
                              </div>
                            </div>
                            <button
                              onClick={() => toggleRItem(item.id, item.is_available)}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                                item.is_available
                                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100'
                              }`}
                              title={item.is_available ? 'Skjul fra meny' : 'Gjør tilgjengelig'}
                            >
                              {item.is_available ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              {item.is_available ? 'Skjul' : 'Vis'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {!items.length && <div className="text-gray-500 text-sm">Ingen varer i denne kategorien.</div>}
                  </div>
                ))}

                {!rMenu.length && <div className="text-gray-500">Menyen er tom for denne restauranten.</div>}
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
